import * as React from 'react';
import { connect, useSelector } from 'react-redux';
import calendar from '../../requests/calendar';
import { CalendarByTypes, CalendarCards } from '../../types/requests';
import { pushBooksAction, setBookProperties, deleteBookAction, setBooksAction } from '../../store/calendar/actions';
import { getCardsArray, getState, CardsByYears } from '../../store/calendar/selectors';
import { LibraryCard, CardProperties, CardPropertyType } from '../../types/LibraryCard';
import saveCard from '../../requests/saveCard';
import saveCardProperties from '../../requests/saveCardProperties';
import { CalendarState } from '../../store/calendar/reducers';
import getLibraryCard from '../../requests/getLibraryCard';
import deleteCard from '../../requests/deleteCard';
import { getUserState } from '../../store/user/selectors';
import { endOfYear, startOfYear } from 'date-fns';

export type CardsManagerCallbacks = {
	cards: LibraryCard[],
	calendar: CalendarState;
	addCard: (card: LibraryCard) => Promise<number>,
	saveCardProperties: (cardId: number, properties: CardProperties) => Promise<boolean>;
	deleteCard: (cardId: number) => Promise<boolean>;
	cardsByDate?: CardsByYears;
	loading?: boolean;
}

interface DispatchProps {
	setBooks: typeof setBooksAction;
	pushBooks: typeof pushBooksAction;
	saveProperties: typeof setBookProperties;
	deleteBook: typeof deleteBookAction;
}

interface Props {
	type?: 'list' | 'calendar';
	children: (callbacks: CardsManagerCallbacks) => React.ReactNode;
	enableWelcomeBook?: boolean;
	welcomeType?: 'common' | 'calendar';
	withoutInitial?: boolean;
	currentDate?: Date;
}

const CardsManager: React.FC<DispatchProps & Props> = props => {
	const [loading, setLoading] = React.useState(false);

	const cards = useSelector(getCardsArray);
	const calendarState = useSelector(getState);
	const userProfile = useSelector(getUserState);
	
	const getCardsRequest = async () => {
		const queries = new Map<string, string>();

		setLoading(true);

		if (props.type === 'calendar') {
			queries.set("join", "true");
		}

		if (props.currentDate) {
			queries.set("startDate", startOfYear(props.currentDate).toISOString());
			queries.set("finishDate", endOfYear(props.currentDate).toISOString());
		}

		const request = await calendar(queries);

		if (request) {
			if (props.type !== 'calendar') {
				const byTypeRequest = request as CalendarByTypes;

				const array = [
					...byTypeRequest.alreadyRead,
					...byTypeRequest.readNow,
					...byTypeRequest.wantRead,
				]

				props.setBooks(array);
			}
			else {
				props.setBooks([...(request as CalendarCards)]);
			}
		}

		setLoading(false);
	};

	const addCard = async (card: LibraryCard): Promise<number> => {
		if (!(userProfile.userId > 0)) {
			window.location.href = '/login';

			return null;
		}

		const request = await saveCard({
			cards: {
				cards: [
					{
						iId: 1,
						cardType: card.cardType,
						book: card.book,
						properties: card.properties
					}
				]
			}
		});

		if (request.data && request.data[1]) {
			getCardsRequest();

			return request.data[1];
		}
		
		return null;
	};

	const getCardById = async (cardId: number) => {
		const request = await getLibraryCard(cardId);

		if (request) {
			props.pushBooks([
				request
			]);
		}
	};

	const saveCardPropertiesCallback = async (cardId: number, properties: CardProperties): Promise<boolean> => {
		const request = await saveCardProperties({
			cardId,
			properties
		});

		if (request.data) {
			getCardsRequest();

			props.saveProperties({
				cardId,
				properties,
				cardType: properties[CardPropertyType.Type] as any
			})

			return request.data;
		}

		return false;
	};

	const deleteCardRequest = async (cardId: number) => {
		const request = await deleteCard({
			cardId
		});

		if (request) {
			props.deleteBook(cardId);
		}
	
		return true;
	};

	React.useEffect(() => {
		if (!props.withoutInitial) {
			getCardsRequest();
		}
	}, [props.type, props.currentDate]);

	const callbacks: CardsManagerCallbacks = {
		cards,
		calendar: calendarState,
		addCard,
		deleteCard: deleteCardRequest,
		saveCardProperties: saveCardPropertiesCallback,
		loading
	};

	return <>
		{props.children(callbacks)}
	</>;
};

export default connect(() => { },
	{
		setBooks: setBooksAction,
		pushBooks: pushBooksAction,
		saveProperties: setBookProperties,
		deleteBook: deleteBookAction
	}
)(CardsManager);